import React, { useState, useEffect, useRef } from "react"; // Import useRef
import PropTypes from "prop-types";
import styles from "./SelectMediaDropdown.module.css";
import { SwitchTransition, CSSTransition } from "react-transition-group";
import MediaSectionElement from "../MediaSectionElement";
import TextInput from "../TextInput";
import SelectColorInput from "../SelectColorInput";
import SpanInput from "../SpanInput";
import Button from "../Button";

/**
 * Interactive component.
 * A dropdown that allows the user to add media to a feed item section.
 */
export function SelectMediaDropdown({
  isModalOpen,
  setIsModalOpen,
  isOpen,
  setIsOpen,
  mediaSettings,
  setMediaSettings,
  index,
}) {
  const transitionRef = useRef(null); // Instantiate ref for transitions

  const [activeColorPicker, setActiveColorPicker] = useState(null);
  const [customColors, setCustomColors] = useState({
    headerCustomColor: "#fff",
    buttonCustomColor: "#fff",
  });

  // Ref for the div that contains color selectors
  const [headerColorRef, setHeaderColorRef] = useState(null);
  const [buttonColorRef, setButtonColorRef] = useState(null);

  useEffect(() => {
    function handleOutsideClick(event) {
      const clickedInsideHeaderColor =
        headerColorRef && headerColorRef.contains(event.target);
      const clickedInsideButtonColor =
        buttonColorRef && buttonColorRef.contains(event.target);

      if (
        activeColorPicker !== "headerColor" &&
        !clickedInsideHeaderColor &&
        activeColorPicker !== "buttonColor" &&
        !clickedInsideButtonColor
      ) {
        setActiveColorPicker(null);
      }
    }
    document.addEventListener("mouseup", handleOutsideClick);
    return () => {
      document.removeEventListener("mouseup", handleOutsideClick);
    };
  }, [headerColorRef, buttonColorRef]);

  //Function to get the input values
  const handleCardInfoChange = (field, value) => {
    setMediaSettings(field, value, index);
  };

  return (
    <>
      <SwitchTransition mode="out-in">
        <CSSTransition
          key={isOpen ? "dropdown" : "button"}
          nodeRef={transitionRef}
          classNames={{
            enter: styles["fade-enter"],
            exit: styles["fade-exit"],
            enterActive: styles["fade-enter-active"],
            exitActive: styles["fade-exit-active"],
          }}
          addEndListener={(done) => {
            const node = transitionRef.current;
            if (node) node.addEventListener("transitionend", done, false);
          }}
        >
          <div ref={transitionRef}>
            {!isOpen ? (
              <div
                className={styles["divider-button"]}
                onClick={() => setIsOpen(index)}
              >
                <span className={styles.svg}>
                  <MediaSectionElement
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                    mediaSettings={mediaSettings}
                  />
                </span>
              </div>
            ) : (
              <div className={styles.dropdown}>
                <div className={styles["button-container"]}>
                  <Button
                    onClick={() => setIsOpen(index)}
                    text={"Done"}
                    className={styles.button}
                  />
                </div>
                <div className={styles.content}>
                  <div className={styles["header-container"]}>
                    <div className={styles["text-input"]}>
                      <TextInput
                        label={"Title"}
                        placeholder={"NEW MEDIA"}
                        value={mediaSettings.header}
                        setValue={(e) => {
                          handleCardInfoChange("header", e.target.value);
                        }}
                      />
                    </div>

                    <div className={styles["header-color"]}>
                      <SelectColorInput
                        setRef={setHeaderColorRef}
                        isOpen={activeColorPicker === "headerColor"}
                        setIsOpen={() => {
                          setActiveColorPicker(
                            activeColorPicker === "headerColor"
                              ? null
                              : "headerColor"
                          );
                        }}
                        customColor={customColors.headerCustomColor}
                        setCustomColor={(value) =>
                          setCustomColors({
                            ...customColors,
                            headerCustomColor: value,
                          })
                        }
                        currentColor={mediaSettings.headerColor}
                        setCurrentColor={(e) => {
                          handleCardInfoChange(
                            "headerColor",
                            `${e.target.title} ${e.target.style.backgroundColor}`
                          );
                        }}
                        labelText={"Text"}
                        className={styles["text-color"]}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>

                  <div className={styles["settings-container"]}>
                    <h5 className={styles.settings}>Settings</h5>
                    <div className={styles["settings-content"]}>
                      <div className={styles["vzaar-format"]}>
                        <div className={styles.vzaar}>
                          <SpanInput
                            currentValue={mediaSettings.id}
                            setCurrentValue={(e) =>
                              handleCardInfoChange("id", e.target.value)
                            }
                            inputType={"text"}
                            inputLabel={"VZaar ID:"}
                            placeholder={"VZaar ID"}
                          />
                        </div>
                        <div className={styles.format}>
                          <SpanInput
                            currentValue={mediaSettings.format ?? "Video"}
                            setCurrentValue={(value) =>
                              handleCardInfoChange("format", value)
                            }
                            inputType={"radio"}
                            inputLabel={"Format:"}
                            radioOptions={["Video", "Audio"]}
                          />
                        </div>
                      </div>
                      <div className={styles["button-color"]}>
                        <SelectColorInput
                          setRef={setButtonColorRef}
                          isOpen={activeColorPicker === "buttonColor"}
                          setIsOpen={() => {
                            setActiveColorPicker(
                              activeColorPicker === "buttonColor"
                                ? null
                                : "buttonColor"
                            );
                          }}
                          customColor={customColors.buttonCustomColor}
                          setCustomColor={(value) =>
                            setCustomColors({
                              ...customColors,
                              buttonCustomColor: value,
                            })
                          }
                          currentColor={mediaSettings.buttonColor}
                          setCurrentColor={(e) => {
                            handleCardInfoChange(
                              "buttonColor",
                              `${e.target.title} ${e.target.style.backgroundColor}`
                            );
                          }}
                          labelText={"Button"}
                          className={styles["text-color"]}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CSSTransition>
      </SwitchTransition>
    </>
  );
}

SelectMediaDropdown.propTypes = {
  /**
   * Object that contains the media item’s configurations.
   */
  mediaSettings: PropTypes.shape({
    header: PropTypes.string,
    headerColor: PropTypes.string,
    id: PropTypes.string,
    format: PropTypes.oneOf(["Video", "Audio"]),
    buttonColor: PropTypes.string,
  }),
  /**
   * Boolean that determines if the play modal is open.
   */
  isModalOpen: PropTypes.bool,
  /**
   * Function that fires when the play button is clicked (opens the media modal).
   */
  setIsModalOpen: PropTypes.func,
  /**
   * Boolean that determines if the dropdown is open.
   */
  isOpen: PropTypes.bool,
  /**
   * Function that controls the dropdown.
   */
  setIsOpen: PropTypes.func,
};

SelectMediaDropdown.defaultProps = {
  mediaSettings: {
    header: "NEW MEDIA",
    format: "Video",
  },
};
